import { FC, useState } from "react";

import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Skeleton, Stack } from "@mui/material";
import { COMMUNICATION_PREFERENCE_QUESTION_TITLE_MAX_LENGTH, CommunicationPreferenceFormType } from "@simplyk/common";

import { CommunicationPreferenceOptionNameModal } from "./CommunicationPreferenceOptionNameModal";
import { CommunicationPreferenceOptionRow } from "./CommunicationPreferenceOptionRow";
import { DeleteCommunicationPreferenceOptionModal } from "./DeleteCommunicationPreferenceOptionModal";
import { CommunicationPreferenceOption, CommunicationPreferenceOptionNameResult } from "./types";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTextField } from "@/dashboard-design-system/TextField";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Add } from "@/icons/outlined";

interface CommunicationPreferencesFormContentProps {
  options: CommunicationPreferenceOption[];
  selectedContactListIds: string[];
  questionTitle: string | null;
  defaultQuestionTitle: string | null;
  isLoading?: boolean;
  canManageOptions?: boolean;
  formType?: CommunicationPreferenceFormType;
  onSelectedContactListIdsChange: (contactListIds: string[]) => void;
  onQuestionTitleChange: (questionTitle: string) => void;
  onCreateOption: (name: string) => Promise<CommunicationPreferenceOptionNameResult>;
  onRenameOption: (contactListId: string, name: string) => Promise<CommunicationPreferenceOptionNameResult>;
  onDeleteOption: (contactListId: string) => Promise<boolean>;
}

type OptionModalState =
  | { mode: "create" }
  | { mode: "rename"; option: CommunicationPreferenceOption }
  | { mode: "delete"; option: CommunicationPreferenceOption }
  | null;

export const CommunicationPreferencesFormContent: FC<CommunicationPreferencesFormContentProps> = ({
  options,
  selectedContactListIds,
  questionTitle,
  defaultQuestionTitle,
  isLoading = false,
  canManageOptions = true,
  formType,
  onSelectedContactListIdsChange,
  onQuestionTitleChange,
  onCreateOption,
  onRenameOption,
  onDeleteOption,
}) => {
  const { t } = useTranslate();
  const [optionModalState, setOptionModalState] = useState<OptionModalState>(null);

  const selectedOptions = selectedContactListIds.flatMap((contactListId) => {
    const option = options.find((candidate) => candidate.contactListId === contactListId);
    return option ? [option] : [];
  });
  const hiddenOptions = options
    .filter((option) => !selectedContactListIds.includes(option.contactListId))
    .sort((first, second) => Number(second.isDefault) - Number(first.isDefault));
  const singleSelectedOption = selectedOptions.length === 1 ? selectedOptions[0] : null;
  const hasMultipleSelectedOptions = selectedOptions.length > 1;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = selectedContactListIds.indexOf(String(active.id));
    const newIndex = selectedContactListIds.indexOf(String(over.id));
    onSelectedContactListIdsChange(arrayMove(selectedContactListIds, oldIndex, newIndex));
  };

  const handleToggleVisibility = (contactListId: string) =>
    onSelectedContactListIdsChange(
      selectedContactListIds.includes(contactListId)
        ? selectedContactListIds.filter((selectedId) => selectedId !== contactListId)
        : [...selectedContactListIds, contactListId]
    );

  return (
    <>
      <Stack
        spacing={2}
        sx={(theme) => ({
          backgroundColor: theme.palette.surface.neutral.supershy,
          borderRadius: theme.radius(1),
          p: 2,
        })}
      >
        <DashboardTextField
          label={t("dashboard", "communicationPreferences.questionTitleLabel")}
          value={questionTitle ?? ""}
          placeholder={defaultQuestionTitle ?? undefined}
          onChange={(event) => onQuestionTitleChange(event.target.value)}
          disabled={questionTitle === null}
          inputProps={{ maxLength: COMMUNICATION_PREFERENCE_QUESTION_TITLE_MAX_LENGTH }}
          helperText={t(
            "dashboard",
            hasMultipleSelectedOptions
              ? "communicationPreferences.questionTitleHelper"
              : "communicationPreferences.questionTitleHelperSingleOption"
          )}
          slotProps={{ inputLabel: { slotProps: { helperText: { variant: "body2", vibe: "text-neutral-supershy" } } } }}
          data-test="communication-preferences-question-title"
        />

        <Stack spacing={1}>
          <Stack>
            <DashboardTypography variant="subtitle2" vibe="text-neutral-moderate">
              {t("dashboard", "communicationPreferences.choicesLabel")}*
            </DashboardTypography>
            <DashboardTypography variant="body2" vibe="text-neutral-supershy">
              {singleSelectedOption
                ? t("dashboard", "communicationPreferences.choicesHelperSingleOption", {
                    name: singleSelectedOption.name,
                  })
                : t("dashboard", "communicationPreferences.choicesHelper")}
            </DashboardTypography>
          </Stack>

          {isLoading ? (
            <Skeleton variant="rounded" height={64} />
          ) : (
            <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={selectedContactListIds} strategy={verticalListSortingStrategy}>
                <Stack spacing={1}>
                  {[...selectedOptions, ...hiddenOptions].map((option) => (
                    <CommunicationPreferenceOptionRow
                      key={option.contactListId}
                      id={option.contactListId}
                      name={option.name}
                      subscriberCount={option.subscriberCount}
                      isVisible={selectedContactListIds.includes(option.contactListId)}
                      isSignUpForm={formType === CommunicationPreferenceFormType.NewsletterForm}
                      onRename={canManageOptions ? () => setOptionModalState({ mode: "rename", option }) : undefined}
                      onDelete={canManageOptions ? () => setOptionModalState({ mode: "delete", option }) : undefined}
                      deleteDisabledTooltip={
                        option.isDefault
                          ? t("dashboard", "communicationPreferences.defaultOptionDeleteDisabledTooltip")
                          : undefined
                      }
                      onToggleVisibility={() => handleToggleVisibility(option.contactListId)}
                    />
                  ))}
                </Stack>
              </SortableContext>
            </DndContext>
          )}
        </Stack>

        <DashboardButton
          startIcon={<Add />}
          onClick={() => setOptionModalState({ mode: "create" })}
          variant="tonal"
          vibe="neutral"
          sx={{ alignSelf: "flex-start" }}
          disabled={isLoading}
          data-test="communication-preferences-add-option"
        >
          {t("dashboard", "communicationPreferences.addOption")}
        </DashboardButton>
      </Stack>

      {optionModalState?.mode === "create" && (
        <CommunicationPreferenceOptionNameModal
          mode="create"
          onClose={() => setOptionModalState(null)}
          onSubmit={onCreateOption}
        />
      )}
      {optionModalState?.mode === "rename" && (
        <CommunicationPreferenceOptionNameModal
          mode="rename"
          initialName={optionModalState.option.name}
          onClose={() => setOptionModalState(null)}
          onSubmit={(name) => onRenameOption(optionModalState.option.contactListId, name)}
        />
      )}
      {optionModalState?.mode === "delete" && (
        <DeleteCommunicationPreferenceOptionModal
          name={optionModalState.option.name}
          subscriberCount={optionModalState.option.subscriberCount}
          onClose={() => setOptionModalState(null)}
          onConfirm={() => onDeleteOption(optionModalState.option.contactListId)}
        />
      )}
    </>
  );
};
